import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import React from 'react'

import { HdpNavigationTab } from '@f-fertighaus/applications/Next/components/common/navigation/tabs/useActiveTabs'
import { AreaSizeIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/AreaSizeIcon'
import { BricksIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/BricksIcon'
import { CakeIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/CakeIcon'
import { HouseQuestionIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/HouseQuestionIcon'
import { HouseSizeIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/HouseSizeIcon'
import { KfnFundingIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/KfnFundingIcon'
import { RoofTypeIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/RoofTypeIcon'
import { RoomNumberIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/RoomNumberIcon'
import { InfoTooltip } from '@f-fertighaus/applications/Next/components/fertighaus/InfoTooltip/InfoTooltip'
import { DEFAULT_LOCALE } from '@f-fertighaus/applications/Next/config/constants'
import {
  DictionaryKey,
  t
} from '@f-fertighaus/applications/Next/public/locales/de'
import { EnergyStandard } from '@f-fertighaus/applications/Next/types/houseDetailsResponse'
import {
  CONSTRUCTION_METHOD_BY_NUMBER,
  EXPANSION_STAGE_BY_NUMBER,
  ExpansionStageValue,
  ROOF_BY_NUMBER
} from '@f-fertighaus/applications/Next/utils/backendStructureMappings'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { isNotEmptyArray } from '@f-fertighaus/applications/Next/utils/typeguards/isNotEmptyArray'
import { isNotEmptyString } from '@f-fertighaus/applications/Next/utils/typeguards/isNotEmptyString'
import { DetailElement } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/common/DetailElement/DetailElement'
import { DetailValueWithTooltip } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/common/DetailElement/DetailElement.styled'
import { HouseDescription } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/OverviewSection/HouseDescription/HouseDescription'

export const OverviewSection = () => {
  const {
    state: {
      living_area_total,
      number_rooms,
      construction_method,
      construction_method_name,
      funding,
      number_storeys,
      expansion_stages,
      energy_standard_name,
      roof_shape_name,
      roof_shape
    }
  } = useHouseDetail()

  const { type: roofShapeType } = ROOF_BY_NUMBER[roof_shape || 1]
  const constructionMethodType =
    CONSTRUCTION_METHOD_BY_NUMBER[construction_method]

  const expansionStageValues = expansion_stages
    ?.sort((a, b) => b - a)
    .map(number => EXPANSION_STAGE_BY_NUMBER[number] as ExpansionStageValue)

  return (
    <SectionWrapper id={`navigation-category-${HdpNavigationTab.overview}`}>
      <OverviewList>
        <DetailElement
          verticalLayout
          icon={AreaSizeIcon}
          name={t(
            `houseDetailPage.sections.details.overview.labels.living_area_total`
          )}>
          <Typography variant='body2'>
            {t(
              `houseDetailPage.sections.details.overview.values.living_area_total`,
              {
                area: Math.round(living_area_total).toLocaleString(
                  DEFAULT_LOCALE
                )
              }
            )}
          </Typography>
        </DetailElement>
        {number_rooms && (
          <DetailElement
            verticalLayout
            icon={RoomNumberIcon}
            name={t(
              `houseDetailPage.sections.details.overview.labels.number_rooms`
            )}>
            <Typography variant='body2'>
              {number_rooms.toLocaleString(DEFAULT_LOCALE)}
            </Typography>
          </DetailElement>
        )}
        {isNotEmptyArray(expansionStageValues) && (
          <DetailElement
            verticalLayout
            icon={HouseQuestionIcon}
            name={t(
              `houseDetailPage.sections.details.overview.labels.expansion_stage_display`
            )}>
            <DetailValueWithTooltip>
              <div>
                <OverviewDetailWithMultipleAnswers variant='body2'>
                  {t(
                    `houseDetailPage.sections.details.overview.values.expansion_stage.${expansionStageValues[0]}` as DictionaryKey
                  )}
                </OverviewDetailWithMultipleAnswers>
              </div>
              <InfoTooltip
                title={t(
                  `houseDetailPage.sections.details.overview.tooltips.expansion_stage.${expansionStageValues[0]}` as DictionaryKey
                )}
                wtPayload={{
                  location: 'details-section',
                  label: expansionStageValues[0]
                }}
              />
            </DetailValueWithTooltip>
          </DetailElement>
        )}
        <DetailElement
          verticalLayout
          icon={BricksIcon}
          name={t(
            `houseDetailPage.sections.details.overview.labels.construction_method_name`
          )}>
          <DetailValueWithTooltip>
            <Typography variant='body2'>{construction_method_name}</Typography>
            <InfoTooltip
              title={t(
                `houseDetailPage.sections.details.overview.tooltips.construction_method.${constructionMethodType}` as DictionaryKey
              )}
              wtPayload={{
                location: 'details-section',
                label: constructionMethodType
              }}
            />
          </DetailValueWithTooltip>
        </DetailElement>
        {number_storeys && (
          <DetailElement
            verticalLayout
            icon={HouseSizeIcon}
            name={t(
              `houseDetailPage.sections.details.overview.labels.number_storeys`
            )}>
            <Typography variant='body2'>
              {number_storeys?.toLocaleString(DEFAULT_LOCALE)}
            </Typography>
          </DetailElement>
        )}
        {funding && (
          <DetailElement
            verticalLayout
            icon={KfnFundingIcon}
            name={t(
              `houseDetailPage.sections.details.overview.labels.funding`
            )}>
            <DetailValueWithTooltip>
              <Typography variant='body2'>
                {t(
                  `houseDetailPage.sections.details.overview.values.funding.${funding}`
                )}
              </Typography>
              <InfoTooltip
                title={t(
                  `houseDetailPage.sections.details.overview.tooltips.funding.${funding}` as DictionaryKey
                )}
                wtPayload={{
                  location: 'details-section',
                  label: funding
                }}
              />
            </DetailValueWithTooltip>
          </DetailElement>
        )}
        {isNotEmptyString<EnergyStandard>(energy_standard_name) && (
          <DetailElement
            verticalLayout
            icon={CakeIcon}
            name={t(
              `houseDetailPage.sections.details.energy_standards.labels.energy_standard`
            )}>
            <DetailValueWithTooltip>
              <div>
                <OverviewDetailWithMultipleAnswers variant='body2'>
                  {t(
                    `houseDetailPage.sections.details.energy_standards.values.${energy_standard_name}` as DictionaryKey
                  )}
                </OverviewDetailWithMultipleAnswers>
              </div>
              <InfoTooltip
                title={t(
                  `houseDetailPage.sections.details.energy_standards.tooltips.${energy_standard_name}` as DictionaryKey
                )}
                wtPayload={{
                  location: 'details-section',
                  label: energy_standard_name
                }}
              />
            </DetailValueWithTooltip>
          </DetailElement>
        )}
        {roof_shape_name && roofShapeType && (
          <DetailElement
            verticalLayout
            icon={RoofTypeIcon}
            name={t(
              `houseDetailPage.sections.details.roof.labels.roof_shape_name`
            )}>
            <DetailValueWithTooltip>
              <Typography variant='body2'>{roof_shape_name}</Typography>
              <InfoTooltip
                title={t(
                  `houseDetailPage.sections.details.roof.tooltips.${roofShapeType}` as DictionaryKey
                )}
                wtPayload={{
                  location: 'details-section',
                  label: roofShapeType
                }}
              />
            </DetailValueWithTooltip>
          </DetailElement>
        )}
      </OverviewList>
      <HouseDescription />
    </SectionWrapper>
  )
}

const OverviewList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`

const OverviewDetailWithMultipleAnswers = styled(Typography)`
  &:not(&:first-of-type) {
    margin-top: ${({ theme }) => theme.spacing(1)};
    color: ${({ theme }) => theme.palette.primary.light};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    &:not(&:first-of-type) {
      display: none;
    }
  }
`

const SectionWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: 0;
    margin-bottom: ${({ theme }) => theme.spacing(8)};
  }
`
